import { envConfig } from '@/lib/env-config';
import { supabase } from '@/lib/supabase';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCloudFunction } from './use-cloud-function';
import { useToast } from './use-toast';

export interface ScribeSection {
  id: string;
  scribe_job_id: string;
  section_name: string;
  original_text_key: string | null;
  original_json_key: string | null;
  modified_json_key: string | null;
  start_time: string | null;
  end_time: string | null;
}

export interface ScribeSectionContent {
  sectionId: string;
  sectionName: string;
  originalTextKey?: string;
  originalJsonKey?: string;
  textContent?: string;
  jsonContent?: any;
  loading: boolean;
  error?: string;
  fetchedAt?: string;
}

export interface UseScribeSectionsEnhancedConfig {
  scribeJobId?: string;
  enabled?: boolean;
  disableCompletionToast?: boolean; // Disable completion toast to avoid conflicts with success dialog
}

export interface UseScribeSectionsEnhancedResult {
  // Realtime state
  isConnected: boolean;
  error: string | null;

  // Sections data
  scribeSections: ScribeSection[];
  sectionContents: Record<string, ScribeSectionContent>;
  isLoadingAnyContent: boolean;
  contentErrors: string[];

  // Section management
  fetchSectionContent: (section: ScribeSection) => Promise<void>;
  getSectionContent: (sectionId: string) => ScribeSectionContent | undefined;
  clearSectionContent: (sectionId: string) => void;
  clearAllContent: () => void;

  // Completion status
  allExpectedSectionsLoaded: boolean;
  expectedSectionsCount: number;
  loadedSectionsCount: number;
}

const EXPECTED_BACKEND_SECTION_NAMES = [
  'CC_HPI',
  'Internal',
  'EXTERNAL',
  'Keratometry',
  'Refraction',
  'Histories',
  'Assessment_Plan',
] as const;

type BackendSectionName = (typeof EXPECTED_BACKEND_SECTION_NAMES)[number];

export function mapBackendToDisplayName(name: string): string {
  const upper = String(name || '').toUpperCase();
  switch (upper) {
    case 'CC_HPI':
      return 'CC & HPI';
    case 'EXTERNAL':
      return 'External';
    case 'ASSESSMENT_PLAN':
      return 'Assessment & Plan';
    default:
      // Capitalize first letter, lower the rest for simple cases
      // and return as-is for multi-case names
      if (upper === 'INTERNAL') return 'Internal';
      if (upper === 'KERATOMETRY') return 'Keratometry';
      if (upper === 'REFRACTION') return 'Refraction';
      if (upper === 'HISTORIES') return 'Histories';
      return name;
  }
}

export const useScribeSectionsEnhanced = (
  config: UseScribeSectionsEnhancedConfig = {}
): UseScribeSectionsEnhancedResult => {
  const {
    scribeJobId,
    enabled = true,
    disableCompletionToast = false,
  } = config;

  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(
    null
  );

  const processedSectionIdsRef = useRef<Set<string>>(new Set());
  const fetchedSectionNamesRef = useRef<Set<string>>(new Set());

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scribeSections, setScribeSections] = useState<ScribeSection[]>([]);
  const [sectionContents, setSectionContents] = useState<
    Record<string, ScribeSectionContent>
  >({});

  // Cloud function and toast
  const { callFunction } = useCloudFunction();
  const { toast } = useToast();

  // Fetch section content using original_json_key
  const fetchSectionContent = useCallback(
    async (section: ScribeSection) => {
      const sectionId = section.id;
      if (!section.original_json_key) {
        console.log('No JSON key available for section:', section.section_name);
        return;
      }
      let shouldFetch = true;
      setSectionContents((prev) => {
        const existing = prev[sectionId];
        // Only fetch if it's not loading and we don't already have content.
        if (
          existing?.loading ||
          existing?.jsonContent ||
          existing?.textContent
        ) {
          shouldFetch = false;
        }
        return prev;
      });

      if (!shouldFetch) {
        console.log(
          `🔄 Skipping fetch for section "${section.section_name}", content already exists or is loading.`
        );
        return;
      }

      const displayName = mapBackendToDisplayName(section.section_name);
      try {
        console.log(
          'Fetching content for section:',
          displayName,
          'with key:',
          section.original_json_key
        );

        const result = await callFunction('getPublicUrlByFileKey', {
          fileKey: section.original_json_key,
        });

        if (!result.success || !result.data) {
          throw new Error(
            (result as any).error || 'Failed to get signed URL for section'
          );
        }

        const { signedUrl } = result.data as { signedUrl?: string };
        if (!signedUrl) {
          throw new Error('No signed URL returned');
        }

        const resp = await fetch(signedUrl);
        if (resp.status === 404) {
          throw new Error('Data not found');
        }
        if (!resp.ok) {
          throw new Error(
            `Failed to fetch section content: ${resp.status} ${resp.statusText}`
          );
        }

        const text = await resp.text();
        let json: any = null;
        try {
          json = JSON.parse(text);
        } catch {
          // keep as textContent only
        }

        setSectionContents((prev) => ({
          ...prev,
          [sectionId]: {
            sectionId,
            sectionName: displayName,
            originalJsonKey: section.original_json_key || undefined,
            originalTextKey: section.original_text_key || undefined,
            loading: false,
            error: undefined,
            fetchedAt: new Date().toISOString(),
            textContent: json ? undefined : text,
            jsonContent: json ?? undefined,
          },
        }));

        // Track fetched section by friendly name
        fetchedSectionNamesRef.current.add(displayName);
        console.log('Successfully fetched content for section:', displayName);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Unknown error';
        console.error('Error fetching section content:', message);
        setSectionContents((prev) => ({
          ...prev,
          [sectionId]: {
            ...(prev[sectionId] || {
              sectionId,
              sectionName: displayName,
            }),
            loading: false,
            error: message,
            fetchedAt: new Date().toISOString(),
          },
        }));
      }
    },
    [callFunction]
  );

  const fetcherRef = useRef(fetchSectionContent);

  useEffect(() => {
    fetcherRef.current = fetchSectionContent;
  }, [fetchSectionContent]);

  // Establish realtime subscription
  useEffect(() => {
    if (!enabled || !scribeJobId) {
      if (subscriptionRef.current) {
        console.log('Tearing down subscription: disabled or no scribeJobId.');
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    const schema = envConfig.supabase.schema || 'eyevantage';
    const channelName = `ev_scribe_sections_job_${scribeJobId}`;

    if (
      subscriptionRef.current &&
      subscriptionRef.current.topic === channelName
    ) {
      console.log('Already subscribed to the correct channel:', channelName);
      return;
    }

    if (subscriptionRef.current) {
      console.log('Job ID changed, cleaning up old subscription.');
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
    }

    setScribeSections([]);
    setSectionContents({});
    fetchedSectionNamesRef.current.clear();

    console.log('Setting up NEW realtime subscription for job:', scribeJobId);
    const channel = supabase.channel(channelName);

    /**
     * Handle INSERT events from the realtime subscription
     * @param payload - The payload from the realtime subscription
     * @returns void
     */
    const handleInsert = (
      payload: RealtimePostgresChangesPayload<ScribeSection>
    ) => {
      console.log('📥 Received INSERT:', payload);
      const newSection = payload.new as ScribeSection;
      if (!newSection?.id) return;

      setScribeSections((prev) => {
        if (prev.some((s) => s.id === newSection.id)) return prev;
        const updated = [newSection, ...prev];
        return updated;
      });

      // NOTE: We don't fetch the section content here on INSERT.
      // Content fetching happens on UPDATE when the original_json_key is available.
      // This is intentional to avoid premature fetching before content is ready.

      setSectionContents((prev) => ({
        ...prev,
        [newSection.id]: {
          sectionId: newSection.id,
          sectionName: mapBackendToDisplayName(newSection.section_name),
          originalJsonKey: newSection.original_json_key || undefined,
          originalTextKey: newSection.original_text_key || undefined,
          loading: true,
          error: undefined,
          fetchedAt: new Date().toISOString(),
        },
      }));
    };

    /**
     * Handle UPDATE events from the realtime subscription
     * @param payload - The payload from the realtime subscription
     * @returns void
     */
    const handleUpdate = (
      payload: RealtimePostgresChangesPayload<ScribeSection>
    ) => {
      console.log('Received UPDATE:', payload);
      const updatedSection = payload.new as ScribeSection;
      if (!updatedSection?.id) return;

      setScribeSections((prev) => {
        const index = prev.findIndex((s) => s.id === updatedSection.id);
        if (index === -1) return [updatedSection, ...prev];
        const updated = [...prev];
        updated[index] = updatedSection;
        return updated;
      });

      if (updatedSection.original_json_key) {
        void fetcherRef.current(updatedSection);
      }
    };

    channel
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema,
          table: 'ev_scribe_sections',
          filter: `scribe_job_id=eq.${scribeJobId}`,
        },
        handleInsert
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema,
          table: 'ev_scribe_sections',
          filter: `scribe_job_id=eq.${scribeJobId}`,
        },
        handleUpdate
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log(
            '✅ Successfully subscribed to sections for job:',
            scribeJobId
          );
          setIsConnected(true);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Subscription error:', err);
          setError(err?.message || 'Subscription error');
          setIsConnected(false);
        } else if (status === 'CLOSED') {
          setError(err?.message || 'Subscription closed');
          setIsConnected(false);
        }
      });

    subscriptionRef.current = channel;

    return () => {
      console.log('Cleaning up sections subscription for:', scribeJobId);
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
      setIsConnected(false);
    };
  }, [enabled, scribeJobId]);

  // Check if all actually created sections are loaded
  const expectedSectionsCount = scribeSections.length; // Count of sections actually created for this recording
  const loadedSectionsCount = scribeSections.filter((section) => {
    const content = sectionContents[section.id];
    return (
      content &&
      !content.loading &&
      (content.jsonContent ||
        content.textContent ||
        content.error ||
        content.fetchedAt)
    );
  }).length;

  const allExpectedSectionsLoaded = useMemo(() => {
    // If no sections have been created yet, we're not done
    if (scribeSections.length === 0) {
      console.log('❌ No sections created yet');
      return false;
    }

    // Check if ALL sections that were actually created have been processed
    // (either have content or finished with error)
    const result = scribeSections.every((section) => {
      const content = sectionContents[section.id];

      // A section is considered "loaded" if:
      // 1. It has content (success case), OR
      // 2. It has finished loading with an error (failure case), OR
      // 3. It has been fetched (regardless of outcome)
      const isComplete = !!(
        content &&
        !content.loading &&
        (content.jsonContent ||
          content.textContent ||
          content.error ||
          content.fetchedAt)
      );

      if (!isComplete) {
        console.log(
          `❌ Section "${section.section_name}" (${section.id}) not complete:`,
          {
            hasContent: !!content,
            loading: content?.loading,
            error: content?.error,
            hasJsonContent: !!content?.jsonContent,
            hasTextContent: !!content?.textContent,
            fetchedAt: content?.fetchedAt,
          }
        );
      }

      return isComplete;
    });

    console.log('✅ allExpectedSectionsLoaded calculation:', {
      result,
      scribeSectionsCount: scribeSections.length,
      sectionContentsCount: Object.keys(sectionContents).length,
      actualSections: scribeSections.map((s) => s.section_name),
      sectionsStatus: scribeSections.map((s) => {
        const content = sectionContents[s.id];
        return {
          name: s.section_name,
          id: s.id,
          hasContent: !!content,
          loading: content?.loading,
          error: content?.error,
          hasData: !!(content?.jsonContent || content?.textContent),
          fetchedAt: content?.fetchedAt,
        };
      }),
    });

    return result;
  }, [scribeSections, sectionContents]);

  // Show completion toast when all actually created sections are loaded (if not disabled)
  useEffect(() => {
    console.log('Completion effect triggered:', {
      allExpectedSectionsLoaded,
      hasSubscription: !!subscriptionRef.current,
      disableCompletionToast,
      actualSectionsCount: scribeSections.length,
      loadedCount: loadedSectionsCount,
    });

    if (allExpectedSectionsLoaded && subscriptionRef.current) {
      console.log(
        'All actually created sections loaded, stopping subscription'
      );
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
      setIsConnected(false);

      // Only show toast if not disabled (to avoid conflict with success dialog)
      if (!disableCompletionToast) {
        toast({
          title: 'AI Processing Complete',
          description: `All ${scribeSections.length} sections loaded successfully.`,
          variant: 'default',
        });
      }
    }
  }, [
    allExpectedSectionsLoaded,
    toast,
    disableCompletionToast,
    scribeSections.length,
    loadedSectionsCount,
  ]);

  // API
  const getSectionContent = useCallback(
    (sectionId: string) => sectionContents[sectionId],
    [sectionContents]
  );

  const clearSectionContent = useCallback((sectionId: string) => {
    setSectionContents((prev) => {
      const updated = { ...prev };
      delete updated[sectionId];
      return updated;
    });
  }, []);

  const clearAllContent = useCallback(() => {
    setSectionContents({});
    processedSectionIdsRef.current.clear();
    fetchedSectionNamesRef.current.clear();
  }, []);

  const isLoadingAnyContent = useMemo(
    () => Object.values(sectionContents).some((c) => c.loading),
    [sectionContents]
  );

  const contentErrors = useMemo(
    () =>
      Object.values(sectionContents)
        .filter((c) => c.error)
        .map((c) => c.error as string),
    [sectionContents]
  );

  return {
    // Realtime state
    isConnected,
    error,

    // Sections data
    scribeSections,
    sectionContents,
    isLoadingAnyContent,
    contentErrors,

    // Section management
    fetchSectionContent,
    getSectionContent,
    clearSectionContent,
    clearAllContent,

    // Completion status
    allExpectedSectionsLoaded,
    expectedSectionsCount,
    loadedSectionsCount,
  };
};

export default useScribeSectionsEnhanced;
